import React from "react";
import {
    GetSalaryDetailsQuery,
    LaaActivityType,
    Mission,
    SalaryType,
} from "../../../__generated__/graphql";
import { useI18n } from "../../../hooks/i18n-hooks";
import { useLazyQuery } from "@apollo/client";
import { GET_SALARY_DETAILS } from "../../../graphql/salaries";
import { STANDARD_WORKING_TIME_CLASSIC_SPEC } from "../../working_time/helpers";
import { IjmSelection } from "../../../utils";

export const useSalaryTypes = () => {
    const i18n = useI18n();
    const salaryType = React.useMemo(
        () => [
            {
                label: i18n.t("screens.user.create_mission_salary.salaryType.gross"),
                value: SalaryType.Gross,
            },
            {
                label: i18n.t("screens.user.create_mission_salary.salaryType.net"),
                value: SalaryType.Net,
            },
            {
                label: i18n.t("screens.user.create_mission_salary.salaryType.invoice"),
                value: SalaryType.Invoice,
            },
        ],
        [i18n],
    );
    return salaryType;
};

export type Salaries = {
    requestedSalaryType: SalaryType;
    invoiceSalary: number;
    grossSalary: number;
    netSalary: number;
    baseSalary: number;
    minBaseSalary: number;
    minGrossSalary: number;
    minNetSalary: number;
    minInvoiceSalary: number;
};

export const useComputeSalary = (
    mission: Mission,
    ijmSelection: IjmSelection | null | undefined,
    setSalaries: (salaries: Salaries | null) => void,
    setVacationdays: (vacationDays: number) => void,
    setHas13thSalary: (has13thSalary: boolean) => void,
) => {
    const i18n = useI18n();
    const [getSalaryDetails] = useLazyQuery(GET_SALARY_DETAILS);
    const [salaryDetails, setSalaryDetails] = React.useState<GetSalaryDetailsQuery | null>(null);
    const [loading, setloading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const computeSalary = React.useCallback(
        async (salary: number, salaryType: SalaryType) => {
            if (!salary || !ijmSelection?.ijmGroupId) {
                setSalaryDetails(null);
                setSalaries(null);
                return;
            }
            setloading(true);
            setError(null);
            try {
                if (mission.currentContract?.cctEditionId && mission.workerInfo?.birthdate) {
                    const result = await getSalaryDetails({
                        variables: {
                            cctEditionId: mission.currentContract.cctEditionId,
                            cctDetailsValues:
                                mission.currentContract.cctDetails?.tempdataCallValues,
                            workerProfileId: mission.workerInfo.id,
                            requestedSalary: Math.round(salary * 100),
                            requestedSalaryType: salaryType,
                            vacationDays: mission.currentContract.vacationDays,
                            has13thSalary: mission.currentContract.has13thSalary,
                            earningType: mission.currentContract.earningType,
                            workingTimeClassicSpec:
                                mission.currentContract.workingTimeClassicSpec ||
                                STANDARD_WORKING_TIME_CLASSIC_SPEC,
                            hoursPerWeekFullTime:
                                mission.currentContract.hoursPerWeekFullTime?.toString(),
                            laaActivityType: mission.laaActivityType || LaaActivityType.Technical,
                            ijmGroupId: ijmSelection?.ijmGroupId,
                        },
                    });
                    if (result.error) {
                        setError(result.error.message);
                    }
                    if (result.data) {
                        setSalaries({
                            requestedSalaryType: result.data.getSalaryDetails.requestedSalaryType,
                            invoiceSalary: result.data.getSalaryDetails.invoiceSalary,
                            grossSalary: result.data.getSalaryDetails.grossSalary,
                            netSalary: result.data.getSalaryDetails.netSalary,
                            baseSalary: result.data.getSalaryDetails.baseSalary,
                            minBaseSalary: result.data.getSalaryDetails.minBaseSalary,
                            minGrossSalary: result.data.getSalaryDetails.minGrossSalary,
                            minNetSalary: result.data.getSalaryDetails.minNetSalary,
                            minInvoiceSalary: result.data.getSalaryDetails.minInvoiceSalary,
                        });
                        setVacationdays(result.data.getSalaryDetails.vacationDays);
                        setHas13thSalary(result.data.getSalaryDetails.has13thSalary);
                        setSalaryDetails(result.data);
                    }
                } else {
                    setError(
                        i18n.t("screens.user.create_mission_salary.compute_salary_missing_data"),
                    );
                }
            } finally {
                setloading(false);
            }
        },
        [
            setSalaries,
            mission.currentContract?.cctEditionId,
            mission.currentContract?.cctDetails?.tempdataCallValues,
            mission.currentContract?.vacationDays,
            mission.currentContract?.has13thSalary,
            mission.currentContract?.earningType,
            mission.currentContract?.workingTimeClassicSpec,
            mission.currentContract?.hoursPerWeekFullTime,
            mission.workerInfo?.birthdate,
            mission.workerInfo.id,
            mission.laaActivityType,
            getSalaryDetails,
            ijmSelection?.ijmGroupId,
            setVacationdays,
            setHas13thSalary,
            i18n,
        ],
    );

    return {
        computeSalary,
        salaryDetails,
        loading,
        error,
    };
};
